import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, BadgeCheck, Lock, Trophy, DollarSign, Video, Calendar } from "lucide-react";

function compact(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
}

export function UserProfileDialog({ userId, open, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) { setData(null); return; }
    setLoading(true); setError(null);
    api.get(`/users/${userId}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => setError(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const avatar = data?.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(data?.username || "U")}&backgroundColor=c8ff00&textColor=000000`;
  const banner = data?.banner_url;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="user-profile-dialog"
        className="max-w-[560px] overflow-hidden border-zinc-200 bg-white p-0 text-zinc-900 dark:border-white/[0.08] dark:bg-[#0a0a0a] dark:text-white"
      >
        {loading ? (
          <div className="flex h-[320px] items-center justify-center text-zinc-500">
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-rose-500">{error}</div>
        ) : !data ? null : (
          <>
            {/* Banner — animated gradient by default, image if available */}
            <div
              data-testid="user-profile-banner"
              className="relative h-[140px] w-full overflow-hidden sm:h-[170px]"
            >
              {banner ? (
                <img src={banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div
                  className="absolute inset-0 bg-[length:200%_200%] animate-[profileGrad_18s_ease_infinite]"
                  style={{ backgroundImage: "linear-gradient(120deg, #0f172a 0%, #312e81 25%, #c8ff00 50%, #831843 75%, #0f172a 100%)" }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
            </div>

            {/* Header */}
            <div className="relative -mt-12 px-6 pb-6">
              <div className="flex items-end gap-4">
                <img
                  src={avatar}
                  alt=""
                  data-testid="user-profile-avatar"
                  className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)] dark:border-[#0a0a0a]"
                />
                <div className="min-w-0 flex-1 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span
                      data-testid="user-profile-username"
                      className="truncate text-lg font-bold text-zinc-900 dark:text-white"
                    >
                      @{data.username}
                    </span>
                    {data.role && data.role !== "clipper" ? (
                      <BadgeCheck size={16} className="text-[#c8ff00]" />
                    ) : null}
                  </div>
                  <div className="text-[12px] uppercase tracking-widest text-zinc-500 dark:text-zinc-500">
                    {data.role || "Clipper"}
                  </div>
                </div>
              </div>

              {/* Bio */}
              {data.is_private ? (
                <div
                  data-testid="user-profile-private"
                  className="mt-5 flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-[13px] text-zinc-600 dark:border-white/[0.06] dark:bg-white/[0.03] dark:text-zinc-400"
                >
                  <Lock size={14} /> This account is private.
                </div>
              ) : data.bio ? (
                <p
                  data-testid="user-profile-bio"
                  className="mt-5 whitespace-pre-line text-[13.5px] leading-relaxed text-zinc-700 dark:text-zinc-300"
                >
                  {data.bio}
                </p>
              ) : (
                <p className="mt-5 text-[12.5px] italic text-zinc-500 dark:text-zinc-500">
                  No bio yet.
                </p>
              )}

              {/* Stats */}
              <div className="mt-5 grid grid-cols-3 gap-2">
                <div
                  data-testid="user-profile-stat-views"
                  className="rounded-xl border border-zinc-200 bg-white p-3 text-center dark:border-white/[0.06] dark:bg-white/[0.025]"
                >
                  <div className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-100 dark:bg-white/[0.05]">
                    <Trophy size={13} className="text-[#c8ff00]" />
                  </div>
                  <div className="text-[15px] font-bold tabular-nums text-zinc-900 dark:text-white">
                    {compact(data.stats?.views || 0)}
                  </div>
                  <div className="text-[10.5px] uppercase tracking-wider text-zinc-500">Views</div>
                </div>
                <div
                  data-testid="user-profile-stat-clips"
                  className="rounded-xl border border-zinc-200 bg-white p-3 text-center dark:border-white/[0.06] dark:bg-white/[0.025]"
                >
                  <div className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-100 dark:bg-white/[0.05]">
                    <Video size={13} className="text-sky-500" />
                  </div>
                  <div className="text-[15px] font-bold tabular-nums text-zinc-900 dark:text-white">
                    {data.stats?.clips || 0}
                  </div>
                  <div className="text-[10.5px] uppercase tracking-wider text-zinc-500">Clips</div>
                </div>
                <div
                  data-testid="user-profile-stat-earnings"
                  className="rounded-xl border border-zinc-200 bg-white p-3 text-center dark:border-white/[0.06] dark:bg-white/[0.025]"
                >
                  <div className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-100 dark:bg-white/[0.05]">
                    <DollarSign size={13} className="text-emerald-500" />
                  </div>
                  <div className="text-[15px] font-bold tabular-nums text-zinc-900 dark:text-white">
                    ${Number(data.stats?.earnings || 0).toFixed(2)}
                  </div>
                  <div className="text-[10.5px] uppercase tracking-wider text-zinc-500">Earned</div>
                </div>
              </div>

              {/* Date Joined */}
              {data.joined_at ? (
                <div
                  data-testid="user-profile-joined"
                  className="mt-3 flex items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.025]"
                >
                  <div className="flex items-center gap-2 text-[12px] text-zinc-600 dark:text-zinc-400">
                    <Calendar size={13} className="opacity-70" />
                    <span>Joined</span>
                  </div>
                  <span className="text-[12.5px] font-semibold text-zinc-900 dark:text-white">
                    {new Date(data.joined_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </span>
                </div>
              ) : null}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UserProfileDialog;
